// Système de composants TRAME. Chaque composant porte texte + icône pour les états, jamais la couleur seule.
import {
  createContext, useCallback, useContext, useEffect, useId, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { Link } from 'react-router';
import {
  AlertTriangle, CheckCircle2, CircleDashed, CircleHelp, CloudOff, FileText, Info, Loader2, Lock, SearchX, Sparkles, X, XCircle,
} from 'lucide-react';
import { definition } from '@shared/glossaire.ts';

// ─── Boutons ───
type VarianteBtn = 'principal' | 'secondaire' | 'danger' | 'fantome';
export function Bouton({ variante = 'secondaire', compact, icone, chargement, children, className = '', ...p }:
  ButtonHTMLAttributes<HTMLButtonElement> & { variante?: VarianteBtn; compact?: boolean; icone?: ReactNode; chargement?: boolean }) {
  const cls = ['btn', variante !== 'secondaire' && `btn-${variante}`, compact && 'btn-compact', className].filter(Boolean).join(' ');
  return (
    <button type="button" className={cls} disabled={p.disabled || chargement} aria-busy={chargement || undefined} {...p}>
      {chargement ? <Loader2 className="tourne" aria-hidden /> : icone}
      {children}
    </button>
  );
}

export function LienBouton({ to, variante = 'secondaire', compact, icone, children }:
  { to: string; variante?: VarianteBtn; compact?: boolean; icone?: ReactNode; children: ReactNode }) {
  const cls = ['btn', variante !== 'secondaire' && `btn-${variante}`, compact && 'btn-compact'].filter(Boolean).join(' ');
  return <Link to={to} className={cls}>{icone}{children}</Link>;
}

// ─── Champs ───
interface ChampBase { libelle: ReactNode; aide?: ReactNode; erreur?: string | null; mono?: boolean }
export function Champ({ libelle, aide, erreur, mono, className, ...p }: ChampBase & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <div className="champ">
      <label className="champ-libelle" htmlFor={id}>{libelle}</label>
      <input id={id} className={`saisie ${mono ? 'saisie-mono' : ''} ${className ?? ''}`} aria-invalid={!!erreur || undefined}
        aria-describedby={aide || erreur ? `${id}-a` : undefined} {...p} />
      {(aide || erreur) && <span id={`${id}-a`} className={erreur ? 'champ-erreur' : 'champ-aide'}>{erreur || aide}</span>}
    </div>
  );
}

export function ChampTexte({ libelle, aide, erreur, ...p }: ChampBase & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <div className="champ">
      <label className="champ-libelle" htmlFor={id}>{libelle}</label>
      <textarea id={id} className="saisie" aria-invalid={!!erreur || undefined} aria-describedby={aide || erreur ? `${id}-a` : undefined} {...p} />
      {(aide || erreur) && <span id={`${id}-a`} className={erreur ? 'champ-erreur' : 'champ-aide'}>{erreur || aide}</span>}
    </div>
  );
}

export function ChampChoix({ libelle, aide, erreur, options, vide, ...p }:
  ChampBase & SelectHTMLAttributes<HTMLSelectElement> & { options: { valeur: string; libelle: string }[]; vide?: string }) {
  const id = useId();
  return (
    <div className="champ">
      <label className="champ-libelle" htmlFor={id}>{libelle}</label>
      <select id={id} className="saisie" aria-invalid={!!erreur || undefined} {...p}>
        {vide !== undefined && <option value="">{vide}</option>}
        {options.map((o) => <option key={o.valeur} value={o.valeur}>{o.libelle}</option>)}
      </select>
      {(aide || erreur) && <span className={erreur ? 'champ-erreur' : 'champ-aide'}>{erreur || aide}</span>}
    </div>
  );
}

// ─── Badges ───
export type Ton = 'neutre' | 'accent' | 'ambre' | 'danger' | 'succes' | 'info';
const ICONES_TON: Record<Ton, ReactNode> = {
  neutre: <CircleDashed aria-hidden />, accent: <CheckCircle2 aria-hidden />, ambre: <AlertTriangle aria-hidden />,
  danger: <XCircle aria-hidden />, succes: <CheckCircle2 aria-hidden />, info: <Info aria-hidden />,
};
export function Badge({ ton = 'neutre', icone, children, titre }: { ton?: Ton; icone?: ReactNode | false; children: ReactNode; titre?: string }) {
  return <span className={`badge badge-${ton}`} title={titre}>{icone === false ? null : icone ?? ICONES_TON[ton]}{children}</span>;
}

// ─── Transparence IA : tout contenu lu ou produit par l'assistance est marqué (discret, factuel, sans pourcentage) ───
export const TEXTE_LU_AUTO = 'Texte lu automatiquement — à vérifier sur l’image de la page';
export const LU_SUR_PHOTO = 'Lu sur la photo — à confirmer';
export function MarqueIa({ texte = TEXTE_LU_AUTO, id }: { texte?: string; id?: string }) {
  return <span className="marque-ia" id={id}><Sparkles aria-hidden />{texte}</span>;
}

/** Valeur inconnue : affichée comme telle, jamais devinée. */
export function Valeur({ v, mono, inconnu = 'Inconnu' }: { v?: ReactNode; mono?: boolean; inconnu?: string }) {
  if (v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)) return <span className="inconnu">{inconnu}</span>;
  return <span className={mono ? 'mono' : undefined}>{Array.isArray(v) ? v.join(', ') : v}</span>;
}

// ─── Mise en page ───
export function EntetePage({ titre, sousTitre, actions, fil }: { titre: ReactNode; sousTitre?: ReactNode; actions?: ReactNode; fil?: ReactNode }) {
  return (
    <header className="entete-page">
      <div className="entete-page-texte">
        {fil}
        <h1>{titre}</h1>
        {sousTitre && <p className="secondaire">{sousTitre}</p>}
      </div>
      {actions && <div className="groupe-btn">{actions}</div>}
    </header>
  );
}

export function Panneau({ titre, actions, children, serre, id, className = '' }:
  { titre?: ReactNode; actions?: ReactNode; children: ReactNode; serre?: boolean; id?: string; className?: string }) {
  const hid = useId();
  return (
    <section className={`panneau ${className}`} aria-labelledby={titre ? hid : undefined} id={id}>
      {(titre || actions) && (
        <div className="panneau-entete">
          {titre && <h2 id={hid} style={{ fontSize: 'var(--t-l)' }}>{titre}</h2>}
          {actions && <div className="groupe-btn">{actions}</div>}
        </div>
      )}
      <div className={serre ? 'panneau-corps-serre' : 'panneau-corps'}>{children}</div>
    </section>
  );
}

// ─── Fil de contexte : site > bâtiment > étage > zone > équipement / dossier ───
export interface NoeudFil { type: string; libelle: string; to?: string }
export function FilContexte({ noeuds }: { noeuds: NoeudFil[] }) {
  if (!noeuds.length) return null;
  return (
    <nav className="fil" aria-label="Contexte">
      <ol>
        {noeuds.map((n, i) => (
          <li key={i}>
            {n.to && i < noeuds.length - 1
              ? <Link className="fil-noeud" to={n.to}><span><span className="fil-type">{n.type} </span>{n.libelle}</span></Link>
              : <span className="fil-noeud" aria-current={i === noeuds.length - 1 ? 'location' : undefined}><span><span className="fil-type">{n.type} </span>{n.libelle}</span></span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

// ─── États ───
export function EtatVide({ icone, titre, texte, action, centre }: { icone?: ReactNode; titre: string; texte?: ReactNode; action?: ReactNode; centre?: boolean }) {
  return (
    <div className={`etat ${centre ? 'etat-centre' : ''}`}>
      {icone && <div className="etat-icone" aria-hidden>{icone}</div>}
      <h3>{titre}</h3>
      {texte && <p>{texte}</p>}
      {action}
    </div>
  );
}

export function EtatErreur({ erreur, reessayer }: { erreur: unknown; reessayer?: () => void }) {
  const e = erreur as any;
  const code = e?.code as string | undefined;
  const icone = code === 'droit_insuffisant' ? <Lock /> : code === 'hors_connexion' ? <CloudOff /> : code === 'introuvable' ? <SearchX /> : <AlertTriangle />;
  const titre = code === 'droit_insuffisant' ? 'Accès non autorisé' : code === 'hors_connexion' ? 'Connexion indisponible'
    : code === 'introuvable' ? 'Élément introuvable' : 'Impossible de charger ces informations';
  return (
    <div className="etat" role="alert">
      <div className="etat-icone" aria-hidden>{icone}</div>
      <h3>{titre}</h3>
      <p>{e?.message ?? 'Une erreur est survenue.'}</p>
      {reessayer && code !== 'droit_insuffisant' && code !== 'introuvable' && <Bouton onClick={reessayer}>Réessayer</Bouton>}
    </div>
  );
}

export function Chargement({ lignes = 3, libelle = 'Chargement…' }: { lignes?: number; libelle?: string }) {
  return (
    <div className="pile" aria-busy="true" aria-live="polite" style={{ padding: 'var(--e-5)' }}>
      <span className="visuellement-cache">{libelle}</span>
      {Array.from({ length: lignes }).map((_, i) => <div key={i} className="squelette" style={{ width: `${90 - i * 15}%`, height: 18 }} />)}
    </div>
  );
}

export function Alerte({ ton = 'info', titre, children, action }: { ton?: 'info' | 'ambre' | 'danger' | 'succes'; titre?: ReactNode; children?: ReactNode; action?: ReactNode }) {
  const icone = ton === 'ambre' ? <AlertTriangle aria-hidden /> : ton === 'danger' ? <XCircle aria-hidden /> : ton === 'succes' ? <CheckCircle2 aria-hidden /> : <Info aria-hidden />;
  return (
    <div className={`alerte alerte-${ton}`} role={ton === 'danger' ? 'alert' : 'status'}>
      {icone}
      <div className="pile" style={{ gap: 4, flex: 1, minWidth: 0 }}>
        {titre && <strong>{titre}</strong>}
        {children && <div className="petit">{children}</div>}
      </div>
      {action}
    </div>
  );
}

export function Progression({ pct, libelle }: { pct: number; libelle?: string }) {
  return (
    <div className="pile" style={{ gap: 4 }}>
      <div className="progression" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={libelle ?? 'Progression'}>
        <span style={{ width: `${pct}%` }} />
      </div>
      {libelle && <span className="tres-petit discret">{libelle}</span>}
    </div>
  );
}

// ─── Source documentaire : ouvre le document à la bonne page ───
export function Source({ versionId, page, titre, localisation, extrait }: { versionId?: string | null; page?: number | null; titre: string; localisation?: string | null; extrait?: string | null }) {
  const texte = `${titre}${page ? ` — p. ${page}` : ''}${localisation ? `, ${localisation}` : ''}`;
  if (!versionId) return <span className="source" title="Source non disponible"><FileText aria-hidden /><span>{texte}</span></span>;
  return (
    <Link className="source" to={`/documentation/lecteur/${versionId}?${new URLSearchParams({ ...(page ? { page: String(page) } : {}), ...(extrait ? { extrait } : {}) })}`} title={`Ouvrir ${texte}`}>
      <FileText aria-hidden /><span>{texte}</span>
    </Link>
  );
}

// ─── Sigle expliqué à la demande (clic ou clavier, jamais au seul survol) ───
export function Sigle({ code, children }: { code: string; children?: ReactNode }) {
  const [ouvert, setOuvert] = useState(false);
  const def = definition(code);
  if (!def) return <>{children ?? code}</>;
  return (
    <span style={{ position: 'relative', display: 'inline' }}>
      <abbr title={def} style={{ textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>{children ?? code}</abbr>
      <button type="button" className="btn-fantome" aria-label={`Définition de ${code}`} aria-expanded={ouvert} onClick={() => setOuvert(!ouvert)}
        style={{ border: 0, background: 'none', padding: '0 2px', cursor: 'pointer', color: 'var(--encre-3)', verticalAlign: 'middle', minHeight: 24 }}>
        <CircleHelp size={15} aria-hidden />
      </button>
      {ouvert && (
        <span role="note" className="panneau petit" style={{ position: 'absolute', zIndex: 40, top: '100%', left: 0, width: 'min(320px, 80vw)', padding: 12,
          boxShadow: 'var(--ombre-superposee)', fontWeight: 400 }}>
          <strong>{code}</strong> — {def}
        </span>
      )}
    </span>
  );
}

// ─── Dialogue (Échap ferme, focus initial, retour du focus) ───
export function Dialogue({ titre, ouvert, fermer, children, pied, large, classe }:
  { titre: string; ouvert: boolean; fermer: () => void; children: ReactNode; pied?: ReactNode; large?: boolean; classe?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const hid = useId();
  useEffect(() => {
    if (!ouvert) return;
    const avant = document.activeElement as HTMLElement | null;
    const premier = ref.current?.querySelector<HTMLElement>('input, select, textarea, button:not([data-fermer])');
    (premier ?? ref.current)?.focus();
    const touche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fermer();
      if (e.key === 'Tab' && ref.current) {
        const f = [...ref.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')];
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    };
    document.addEventListener('keydown', touche);
    return () => { document.removeEventListener('keydown', touche); avant?.focus?.(); };
  }, [ouvert]);
  if (!ouvert) return null;
  return (
    <div className="voile" onMouseDown={(e) => { if (e.target === e.currentTarget) fermer(); }}>
      <div ref={ref} className={`dialogue ${large ? 'dialogue-large' : ''} ${classe ?? ''}`} role="dialog" aria-modal="true" aria-labelledby={hid} tabIndex={-1}>
        <div className="dialogue-entete">
          <h2 id={hid} style={{ fontSize: 'var(--t-l)' }}>{titre}</h2>
          <button type="button" className="btn btn-fantome btn-icone" onClick={fermer} aria-label="Fermer" data-fermer><X aria-hidden /></button>
        </div>
        <div className="dialogue-corps">{children}</div>
        {pied && <div className="dialogue-pied">{pied}</div>}
      </div>
    </div>
  );
}

/** Confirmation des actions destructrices : le bouton nomme exactement l'action. */
export function useConfirmation() {
  const [etat, setEtat] = useState<{ titre: string; texte: ReactNode; action: string; resoudre: (ok: boolean) => void } | null>(null);
  const confirmer = useCallback((titre: string, texte: ReactNode, action: string) =>
    new Promise<boolean>((resoudre) => setEtat({ titre, texte, action, resoudre })), []);
  const fin = (ok: boolean) => { etat?.resoudre(ok); setEtat(null); };
  const element = (
    <Dialogue titre={etat?.titre ?? ''} ouvert={!!etat} fermer={() => fin(false)}
      pied={<><Bouton onClick={() => fin(false)}>Annuler</Bouton><Bouton variante="danger" onClick={() => fin(true)}>{etat?.action}</Bouton></>}>
      <p>{etat?.texte}</p>
    </Dialogue>
  );
  return { confirmer, element };
}

// ─── Notifications ───
type Notif = { id: number; texte: string; erreur?: boolean };
const CtxNotif = createContext<(texte: string, erreur?: boolean) => void>(() => {});
export function FournisseurNotifications({ children }: { children: ReactNode }) {
  const [liste, setListe] = useState<Notif[]>([]);
  const notifier = useCallback((texte: string, erreur = false) => {
    const id = Date.now() + Math.random();
    setListe((l) => [...l, { id, texte, erreur }]);
    setTimeout(() => setListe((l) => l.filter((n) => n.id !== id)), erreur ? 8000 : 4000);
  }, []);
  return (
    <CtxNotif.Provider value={notifier}>
      {children}
      <div className="notifications" aria-live="polite" role="status">
        {liste.map((n) => (
          <div key={n.id} className={`notification ${n.erreur ? 'notification-erreur' : ''}`}>
            {n.erreur ? <XCircle size={18} aria-hidden /> : <CheckCircle2 size={18} aria-hidden />}<span>{n.texte}</span>
          </div>
        ))}
      </div>
    </CtxNotif.Provider>
  );
}
export const useNotifier = () => useContext(CtxNotif);

// ─── Onglets accessibles ───
export function Onglets<T extends string>({ onglets, actif, changer, libelle }:
  { onglets: { id: T; libelle: ReactNode; compteur?: number }[]; actif: T; changer: (id: T) => void; libelle: string }) {
  // Sur téléphone, la barre d'onglets défile horizontalement : l'onglet actif est ramené dans la vue (jamais caché à droite).
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const barre = ref.current;
    const el = barre?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!barre || !el || barre.scrollWidth <= barre.clientWidth) return;
    const g = el.getBoundingClientRect().left - barre.getBoundingClientRect().left + barre.scrollLeft;
    if (g < barre.scrollLeft || g + el.offsetWidth > barre.scrollLeft + barre.clientWidth) barre.scrollLeft = Math.max(0, g - 16);
  }, [actif]);
  return (
    <div ref={ref} className="onglets" role="tablist" aria-label={libelle}>
      {onglets.map((o) => (
        <button key={o.id} type="button" role="tab" className="onglet" aria-selected={o.id === actif} onClick={() => changer(o.id)}
          onKeyDown={(e) => {
            const i = onglets.findIndex((x) => x.id === actif);
            if (e.key === 'ArrowRight') changer(onglets[(i + 1) % onglets.length].id);
            if (e.key === 'ArrowLeft') changer(onglets[(i - 1 + onglets.length) % onglets.length].id);
          }}>
          {o.libelle}{o.compteur !== undefined && <span className="compteur">{o.compteur}</span>}
        </button>
      ))}
    </div>
  );
}

// ─── Indicateur d'enregistrement automatique ───
export type EtatEnregistrement = 'enregistre' | 'en_cours' | 'modifie' | 'erreur' | 'local';
export function IndicateurEnregistrement({ etat, quand }: { etat: EtatEnregistrement; quand?: string }) {
  const t: Record<EtatEnregistrement, ReactNode> = {
    enregistre: <><CheckCircle2 aria-hidden /> Enregistré{quand ? ` ${quand}` : ''}</>,
    en_cours: <><Loader2 aria-hidden className="tourne" /> Enregistrement…</>,
    modifie: <><CircleDashed aria-hidden /> Modifications non enregistrées</>,
    erreur: <><AlertTriangle aria-hidden style={{ color: 'var(--danger)' }} /> Échec de l’enregistrement — nouvelle tentative</>,
    local: <><CloudOff aria-hidden /> Brouillon gardé sur cet appareil</>,
  };
  return <span className="enregistrement" role="status" aria-live="polite">{t[etat]}</span>;
}

/** Texte d'extrait surligné sans HTML : le serveur encadre les termes trouvés par \u0001 … \u0002. */
export const DEBUT_SURLIGNE = '\u0001';
export const FIN_SURLIGNE = '\u0002';
export function Surligne({ texte }: { texte: string }) {
  const morceaux = texte.split(/(\u0001[^\u0002]*\u0002)/g);
  return <>{morceaux.map((m, i) => m.startsWith(DEBUT_SURLIGNE)
    ? <mark key={i}>{m.slice(1, -1)}</mark>
    : <span key={i}>{m.replace(/[\u0001\u0002]/g, '')}</span>)}</>;
}
